import { WithholdingTransactionType } from "@wingspanhq/bookkeeping/dist/lib/interfaces/withholdingTransaction";
import {
  toWSDateString,
  WSInfiniteScroll,
  WSList,
  WSTable,
  WSTableItem,
  WSText
} from "@wingspanhq/fe-component-library";
import flatten from "lodash/flatten";
import React, { useMemo } from "react";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { useQueryListInternalAccountTransactions } from "../../../../query/banking/queries/useQueryListInternalAccountTransactions";
import { useQueryWithholdingTransactions } from "../../../../query/taxes/queries";
import { WSQueries } from "../../../../query/WSQuery";
import { selectorActiveTaxWithholdingAccount } from "../../../../shared/selectors/selectorActiveTaxWithholdingAccount";
import { TWTransactionTableItem } from "./types";
import { columns } from "./columns";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { PendingWithholdingStatus } from "@wingspanhq/bookkeeping/dist/lib/interfaces";

const formatTaxPaymentTitle = (
  prefix: string,
  labels?: { quarter?: string; year?: string }
): string => {
  let rowTitle = prefix;
  if (labels?.quarter && labels?.year) {
    rowTitle = `${rowTitle} - ${labels.quarter} ${labels.year}`;
  }

  return rowTitle;
};

export const TaxesTransactions: React.FC = () => {
  const queryWithholdingTransactions = useQueryWithholdingTransactions();
  const queryInternalAccounts = useQueryInternalAccounts();
  const internalAccount = queryInternalAccounts.data?.find(
    selectorActiveTaxWithholdingAccount
  );
  const queryPendingTransactions = useQueryListInternalAccountTransactions(
    internalAccount?.internalAccountId!,
    {
      "filter[status]": "Pending"
    },
    {
      enabled: !!internalAccount
    }
  );

  const queryProcessingTransactions = useQueryListInternalAccountTransactions(
    internalAccount?.internalAccountId!,
    {
      "filter[status]": "Processing"
    },
    {
      enabled: !!internalAccount
    }
  );

  const internalPendingTransactions = useMemo(() => {
    return (queryPendingTransactions.data?.pages || []).flatMap(
      page => page.transactions
    );
  }, [queryPendingTransactions.data?.pages]);

  // Scheduled Tax payment section
  const scheduledTransactions = useMemo(() => {
    const mappedPendingTransactions = internalPendingTransactions
      .filter(
        transaction =>
          transaction.status === PendingWithholdingStatus.Pending &&
          (transaction.type as WithholdingTransactionType) ===
            WithholdingTransactionType.TaxPayment
      )
      .map(transaction => ({
        transactionId: transaction.transactionId,
        amount: transaction.amount,
        currency: transaction.currency,
        date: transaction.date,
        type: transaction.type as WithholdingTransactionType,
        labels: transaction.labels as { [key: string]: string }
      }));

    return mappedPendingTransactions.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [internalPendingTransactions]);

  // Pending and processing transactions
  const internalProcessingTransactions = useMemo(() => {
    return (queryProcessingTransactions.data?.pages || []).flatMap(
      page => page.transactions
    );
  }, [queryProcessingTransactions.data?.pages]);

  const pendingTransactions = useMemo(() => {
    const mappedPendingTransactions = internalProcessingTransactions
      .filter(transaction => transaction.status === "Processing")
      .map(transaction => ({
        transactionId: transaction.transactionId,
        amount: transaction.amount,
        currency: transaction.currency,
        status: transaction.status,
        date: transaction.date,
        type: transaction.type as WithholdingTransactionType,
        labels: transaction.labels as { [key: string]: string }
      }));

    // add pending transactions that arent tax payments
    mappedPendingTransactions.push(
      ...internalPendingTransactions
        .filter(
          transaction =>
            transaction.status === PendingWithholdingStatus.Pending &&
            (transaction.type as WithholdingTransactionType) !==
              WithholdingTransactionType.TaxPayment
        )
        .map(transaction => ({
          transactionId: transaction.transactionId,
          amount: transaction.amount,
          currency: transaction.currency,
          status: transaction.status,
          date: transaction.date,
          type: transaction.type as WithholdingTransactionType,
          labels: transaction.labels as { [key: string]: string }
        }))
    );

    return mappedPendingTransactions.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [internalProcessingTransactions, internalPendingTransactions]);

  return (
    <WSQueries
      queries={{
        queryWithholdingTransactions,
        queryPendingTransactions
      }}
    >
      {({ queryWithholdingTransactionsData }) => {
        const transactionList = flatten(
          queryWithholdingTransactionsData.pages || []
        );

        const tableDataPostedTransactions: WSTableItem<TWTransactionTableItem>[] =
          transactionList.map(transaction => {
            return {
              data: {
                title:
                  transaction.type === WithholdingTransactionType.TaxPayment
                    ? formatTaxPaymentTitle(
                        "Payment to IRS",
                        transaction.labels
                      )
                    : transaction.invoiceNumber
                    ? `Set aside from Invoice #${transaction.invoiceNumber}`
                    : transaction.amount < 0
                    ? "Deposit"
                    : "Withdraw",
                description: transaction.invoiceNumber,
                date: transaction.createdAt,
                amount: transaction.amount * -1,
                currency: transaction.currency,
                isPending: false,
                fromWithholding: false,
                transactionId: transaction.withholdingTransactionId,
                type: transaction.type,
                quarter: transaction.labels?.quarter as string | undefined,
                year: transaction.labels?.year as string | undefined
              },
              id: transaction.withholdingTransactionId
            };
          });

        if (
          pendingTransactions.length === 0 &&
          tableDataPostedTransactions.length === 0
        ) {
          return null;
        }

        return (
          <WSList gap="XL">
            <WSText.Paragraph weight="medium">Transactions</WSText.Paragraph>

            <WSTable<TWTransactionTableItem>
              tableData={scheduledTransactions.map(transaction => {
                return {
                  data: {
                    isPending: true,
                    amount: transaction.amount * -1,
                    title:
                      transaction.type === WithholdingTransactionType.TaxPayment
                        ? formatTaxPaymentTitle(
                            "Scheduled payment to IRS",
                            transaction.labels
                          )
                        : transaction.amount < 0
                        ? "Deposit"
                        : "Withdraw",
                    date: transaction.date,
                    currency: transaction.currency as Currency,
                    fromWithholding: true,
                    transactionId: transaction.transactionId,
                    type: transaction.type as WithholdingTransactionType,
                    quarter: transaction.labels?.quarter,
                    year: transaction.labels?.year
                  },
                  id: transaction.transactionId
                };
              })}
              columns={columns}
              showHeader
              showHeaderForEmpty
              forceShowGroups
              getGroupName={() => ({
                text: "Scheduled (future)",
                colorBackground: "amber50"
              })}
            />

            <WSTable<TWTransactionTableItem>
              tableData={pendingTransactions.map(transaction => {
                return {
                  data: {
                    isPending: false,
                    amount: transaction.amount * -1,
                    title:
                      transaction.type === WithholdingTransactionType.TaxPayment
                        ? formatTaxPaymentTitle(
                            "Payment to IRS",
                            transaction.labels
                          )
                        : transaction.amount < 0
                        ? "Deposit"
                        : "Withdraw",
                    date: transaction.date,
                    currency: transaction.currency as Currency,
                    fromWithholding: true,
                    transactionId: transaction.transactionId,
                    type: transaction.type as WithholdingTransactionType,
                    quarter: transaction.labels?.quarter,
                    year: transaction.labels?.year
                  },
                  id: transaction.transactionId
                };
              })}
              columns={columns}
              showHeader={false}
              showHeaderForEmpty={false}
              forceShowGroups
              getGroupName={() => ({
                text: "Pending (processing)",
                colorBackground: "amber50"
              })}
            />

            <WSInfiniteScroll
              onLoad={() => {
                queryWithholdingTransactions.fetchNextPage();
              }}
              loadMore={transactionList.length > 0}
              endOfList={!queryWithholdingTransactions.hasNextPage}
              loading={!!queryWithholdingTransactions.isFetchingNextPage}
            >
              <WSTable<TWTransactionTableItem>
                tableData={tableDataPostedTransactions}
                columns={columns}
                showHeader={false}
                showHeaderForEmpty={false}
                forceShowGroups
                getGroupName={() => "Posted"}
                bottomMessage={
                  !pendingTransactions.length &&
                  !scheduledTransactions.length &&
                  !tableDataPostedTransactions.length
                    ? "No activity yet"
                    : undefined
                }
              />
            </WSInfiniteScroll>
          </WSList>
        );
      }}
    </WSQueries>
  );
};
